import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, Upload, Check, Loader } from 'lucide-react';

const AICareerRoadmapPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    it_experience: '',
    tech_comfort: '',
    career_goal: '',
    timeline: '',
    time_commitment: '',
    background: '',
    skill_linux: 3,
    skill_networking: 3,
    skill_cloud: 3,
    skill_git: 3,
    skill_troubleshooting: 3,
    skill_scripting: 3,
    skill_documentation: 3,
    tools_used: '',
    projects_count: 0,
    interview_confidence: 3,
    learning_preference: '',
    role_preference: '',
    resume_text: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);

  const questions = [
    {
      id: 'it_experience',
      question: 'What is your current IT experience level?',
      type: 'select',
      options: [
        'No experience - Complete beginner',
        '0-6 months - Just starting out',
        '6-12 months - Basic knowledge',
        '1-2 years - Some experience',
        '2+ years - Experienced'
      ]
    },
    {
      id: 'tech_comfort',
      question: 'How comfortable are you with technology?',
      type: 'select',
      options: [
        'Not comfortable - Need help with basics',
        'Somewhat comfortable - Can use common tools',
        'Comfortable - Can troubleshoot issues',
        'Very comfortable - Tech-savvy',
        'Expert - Deep technical knowledge'
      ]
    },
    {
      id: 'career_goal',
      question: 'What is your primary career goal?',
      type: 'select',
      options: [
        'Get my first IT job',
        'Switch to a different IT role',
        'Advance to a senior position',
        'Become a specialist (cloud/DevOps/security)',
        'Start freelancing or consulting'
      ]
    },
    {
      id: 'timeline',
      question: 'When do you want to get a job?',
      type: 'select',
      options: [
        'As soon as possible (1-3 months)',
        'Within 6 months',
        'Within 1 year',
        'No specific timeline - learning for growth'
      ]
    },
    {
      id: 'time_commitment',
      question: 'How many hours per week can you dedicate to learning?',
      type: 'select',
      options: [
        '5-10 hours/week',
        '10-15 hours/week',
        '15-20 hours/week',
        '20+ hours/week (full-time learning)'
      ]
    },
    {
      id: 'background',
      question: 'What is your educational/professional background?',
      type: 'textarea',
      placeholder: 'e.g., Bachelor in Commerce, worked in retail for 5 years...'
    },
    {
      id: 'skills',
      question: 'Rate your current skills (1 = Beginner, 5 = Expert)',
      type: 'skills-rating',
      skills: [
        { key: 'skill_linux', label: 'Linux' },
        { key: 'skill_networking', label: 'Networking' },
        { key: 'skill_cloud', label: 'Cloud Platforms' },
        { key: 'skill_git', label: 'Git/Version Control' },
        { key: 'skill_troubleshooting', label: 'Troubleshooting' },
        { key: 'skill_scripting', label: 'Scripting/Automation' },
        { key: 'skill_documentation', label: 'Documentation' }
      ]
    },
    {
      id: 'tools_used',
      question: 'Which tools/technologies have you used before?',
      type: 'textarea',
      placeholder: 'e.g., VS Code, Docker, AWS, Python, Excel...'
    },
    {
      id: 'projects_count',
      question: 'How many IT projects have you completed?',
      type: 'select',
      options: ['0', '1-2', '3-5', '5-10', '10+']
    },
    {
      id: 'interview_confidence',
      question: 'How confident are you in IT interviews? (1-5)',
      type: 'slider',
      min: 1,
      max: 5
    },
    {
      id: 'learning_preference',
      question: 'Do you prefer hands-on learning or theory?',
      type: 'select',
      options: [
        'Hands-on - Learning by doing',
        'Mixed - Balance of both',
        'Mostly theory - Understand concepts first'
      ]
    },
    {
      id: 'role_preference',
      question: 'Do you want a role with more people interaction or technical work?',
      type: 'select',
      options: [
        'More people interaction (support)',
        'Balanced',
        'More technical/engineering'
      ]
    },
    {
      id: 'resume_text',
      question: 'Upload your resume (optional - helps us provide better recommendations)',
      type: 'file',
      placeholder: 'Upload PDF or paste text...'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    // Set a timeout to show fallback after 30 seconds
    const timeoutId = setTimeout(() => {
      // Show a fallback result if GPT-5 is taking too long or budget is exceeded
      setAssessmentResult({
        recommended_track: formData.it_experience.includes('No experience') || formData.it_experience.includes('0-6 months') 
          ? 'IT Job Ready System Engineer' 
          : 'Cloud and DevOps Job Ready',
        confidence_level: 'medium',
        reasoning: 'Based on your experience level and career goals, we recommend starting with this program.',
        readiness_score: {
          overall: 75,
          technical_skills: 65,
          practical_experience: 70,
          interview_readiness: 60,
          time_commitment: 85
        },
        action_plan_30_days: [
          'Set up your learning environment and schedule',
          'Complete the first module of fundamentals',
          'Join the CareerLift community and introduce yourself',
          'Start building your first practice project',
          'Schedule a 1:1 session with your mentor'
        ],
        roadmap_90_days: {
          weeks_1_4: ['Complete foundation modules', 'Build first mini-project'],
          weeks_5_8: ['Advanced topics and hands-on labs', 'Start portfolio project'],
          weeks_9_12: ['Complete final project', 'Interview preparation']
        },
        six_month_expectations: {
          skills_mastered: ['System Administration', 'Networking', 'Troubleshooting', 'Documentation'],
          projects_completed: 3,
          job_readiness: 'entry-level',
          estimated_salary_range: '$65,000 - $80,000 AUD'
        }
      });
      setIsLoading(false);
    }, 30000);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/assess-career`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      clearTimeout(timeoutId); // Clear timeout if response is received

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // If budget exceeded or server error, show fallback result
        if (response.status === 500 || errorData.detail?.includes('budget')) {
          setAssessmentResult({
            recommended_track: formData.it_experience.includes('No experience') || formData.it_experience.includes('0-6 months') 
              ? 'IT Job Ready System Engineer' 
              : 'Cloud and DevOps Job Ready',
            confidence_level: 'medium',
            reasoning: 'Based on your experience level and goals, we recommend this program as your best starting point.',
            readiness_score: {
              overall: 78,
              technical_skills: 70,
              practical_experience: 72,
              interview_readiness: 65,
              time_commitment: 90
            },
            action_plan_30_days: [
              'Set up your development environment and tools',
              'Complete orientation and first learning module',
              'Connect with your assigned mentor',
              'Start your first hands-on project',
              'Join weekly group sessions'
            ],
            roadmap_90_days: {
              weeks_1_4: ['Foundation skills and setup', 'First certification prep'],
              weeks_5_8: ['Intermediate concepts', 'Build portfolio projects'],
              weeks_9_12: ['Advanced topics', 'Interview readiness']
            },
            six_month_expectations: {
              skills_mastered: formData.it_experience.includes('No experience')
                ? ['IT Fundamentals', 'Help Desk Support', 'Windows/Linux Admin', 'Networking Basics']
                : ['Cloud Architecture', 'DevOps Tools', 'Infrastructure as Code', 'CI/CD Pipelines'],
              projects_completed: formData.it_experience.includes('No experience') ? 3 : 5,
              job_readiness: 'entry-level',
              estimated_salary_range: formData.it_experience.includes('No experience') 
                ? '$65,000 - $85,000 AUD'
                : '$90,000 - $120,000 AUD'
            }
          });
          setIsLoading(false);
          return;
        }
        
        throw new Error('Assessment failed');
      }

      const result = await response.json();
      setAssessmentResult(result.assessment);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Show fallback result instead of error
      setAssessmentResult({
        recommended_track: formData.it_experience.includes('No experience') || formData.it_experience.includes('0-6 months') 
          ? 'IT Job Ready System Engineer' 
          : 'Cloud and DevOps Job Ready',
        reasoning: 'Based on your responses, this program matches your experience level and career goals.',
        readiness_score: {
          overall: 75,
          technical_skills: 70,
          practical_experience: 68,
          interview_readiness: 60,
          time_commitment: 85
        },
        action_plan_30_days: [
          'Review your program curriculum and set clear goals',
          'Complete your first week of coursework',
          'Schedule your first mentor session',
          'Set up your development environment',
          'Start networking with fellow students'
        ],
        roadmap_90_days: {
          weeks_1_4: ['Core fundamentals', 'First projects'],
          weeks_5_8: ['Advanced concepts', 'Portfolio building'],
          weeks_9_12: ['Final projects', 'Job preparation']
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const currentValue = formData[question.id];

    if (question.type === 'select') {
      return (
        <select
          value={currentValue}
          onChange={(e) => handleInputChange(question.id, e.target.value)}
          className="w-full p-4 border rounded-xl text-base focus:outline-none focus:ring-2"
          style={{ borderColor: '#E5E7EB', color: '#0F172A' }}
        >
          <option value="">Select an option...</option>
          {question.options.map((opt, idx) => (
            <option key={idx} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (question.type === 'textarea') {
      return (
        <textarea
          value={currentValue}
          onChange={(e) => handleInputChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          rows={4}
          className="w-full p-4 border rounded-xl text-base focus:outline-none focus:ring-2"
          style={{ borderColor: '#E5E7EB', color: '#0F172A' }}
        />
      );
    }

    if (question.type === 'skills-rating') {
      return (
        <div className="space-y-4">
          {question.skills.map((skill) => (
            <div key={skill.key}>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#4B5563' }}>
                {skill.label}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={formData[skill.key]}
                onChange={(e) => handleInputChange(skill.key, parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: '#6B7280' }}>
                <span>Beginner</span>
                <span className="font-bold">{formData[skill.key]}</span>
                <span>Expert</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (question.type === 'slider') {
      return (
        <div>
          <input
            type="range"
            min={question.min}
            max={question.max}
            value={currentValue}
            onChange={(e) => handleInputChange(question.id, parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs mt-2" style={{ color: '#6B7280' }}>
            <span>Not confident</span>
            <span className="font-bold text-lg">{currentValue}</span>
            <span>Very confident</span>
          </div>
        </div>
      );
    }

    if (question.type === 'file') {
      return (
        <div>
          <textarea
            value={currentValue}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder="Paste your resume text here (optional)..."
            rows={6}
            className="w-full p-4 border rounded-xl text-base focus:outline-none focus:ring-2"
            style={{ borderColor: '#E5E7EB', color: '#0F172A' }}
          />
          <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
            ⓘ Tip: Paste your resume text for more personalized recommendations
          </p>
        </div>
      );
    }

    return null;
  };

  const ResultsView = ({ result }) => (
    <div className="max-w-5xl mx-auto">
      {/* Recommended Track */}
      <div className="bg-white rounded-2xl p-8 mb-8 border" style={{ borderColor: '#E5E7EB' }}>
        <div className="text-center mb-6">
          <div className="inline-block px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#10B981', color: 'white' }}>
            <span className="font-semibold">Recommended Path</span>
          </div>
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#0F172A' }}>
            {result.recommended_track}
          </h2>
          <p className="text-lg" style={{ color: '#4B5563' }}>
            {result.reasoning}
          </p>
        </div>
      </div>

      {/* Readiness Score */}
      {result.readiness_score && (
        <div className="bg-white rounded-2xl p-8 mb-8 border" style={{ borderColor: '#E5E7EB' }}>
          <h3 className="text-2xl font-bold mb-6" style={{ color: '#0F172A' }}>Your Readiness Score</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(result.readiness_score).map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-3xl font-bold mb-1" style={{ color: '#335CFF' }}>{value}%</div>
                <div className="text-sm capitalize" style={{ color: '#6B7280' }}>
                  {key.replace(/_/g, ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 30-Day Action Plan */}
      {result.action_plan_30_days && (
        <div className="bg-white rounded-2xl p-8 mb-8 border" style={{ borderColor: '#E5E7EB' }}>
          <h3 className="text-2xl font-bold mb-6" style={{ color: '#0F172A' }}>Your 30-Day Action Plan</h3>
          <ul className="space-y-3">
            {result.action_plan_30_days.map((action, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-1" style={{ color: '#10B981' }} />
                <span style={{ color: '#4B5563' }}>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 90-Day Roadmap */}
      {result.roadmap_90_days && (
        <div className="bg-white rounded-2xl p-8 mb-8 border" style={{ borderColor: '#E5E7EB' }}>
          <h3 className="text-2xl font-bold mb-6" style={{ color: '#0F172A' }}>90-Day Learning Roadmap</h3>
          <div className="space-y-6">
            {Object.entries(result.roadmap_90_days).map(([period, focuses]) => (
              <div key={period}>
                <h4 className="font-semibold mb-2 capitalize" style={{ color: '#335CFF' }}>
                  {period.replace(/_/g, ' ').replace(/weeks (\d+) (\d+)/i, 'Weeks $1-$2')}
                </h4>
                <ul className="space-y-2">
                  {focuses.map((focus, idx) => (
                    <li key={idx} className="ml-4" style={{ color: '#4B5563' }}>• {focus}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="text-center">
        <button
          onClick={() => navigate('/contact')}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all"
          style={{ backgroundColor: '#335CFF', color: 'white' }}
        >
          Get Started with CareerLift
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Loader size={48} className="animate-spin mx-auto mb-4" style={{ color: '#335CFF' }} />
            <p className="text-xl font-semibold" style={{ color: '#0F172A' }}>Analyzing your assessment...</p>
            <p className="text-base mt-2" style={{ color: '#6B7280' }}>This will take just a moment</p>
          </div>
        </div>
      </div>
    );
  }

  if (assessmentResult) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="py-20 px-4">
          <ResultsView result={assessmentResult} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      <div className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0F172A' }}>
              AI Career Roadmap Assessment
            </h1>
            <p className="text-xl" style={{ color: '#4B5563' }}>
              Answer a few questions and get a personalized IT career roadmap powered by AI
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold" style={{ color: '#6B7280' }}>
                Question {currentStep + 1} of {questions.length}
              </span>
              <span className="text-sm font-semibold" style={{ color: '#335CFF' }}>
                {Math.round(((currentStep + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${((currentStep + 1) / questions.length) * 100}%`,
                  backgroundColor: '#335CFF'
                }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl p-8 mb-6 border" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#0F172A' }}>
              {questions[currentStep].question}
            </h2>
            {renderQuestion(questions[currentStep])}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="px-6 py-3 rounded-xl font-semibold border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#E5E7EB', color: '#4B5563' }}
            >
              Previous
            </button>
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all"
              style={{ backgroundColor: '#335CFF', color: 'white' }}
            >
              {currentStep === questions.length - 1 ? 'Get My Roadmap' : 'Next'}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AICareerRoadmapPage;
